import React from 'react';
import PropTypes from 'prop-types';
import { TextField as MUITextField } from '@mui';
import { withStyles } from 'tss-react/mui';
import * as colors from '@constants/colors';
import { detailEdit } from '@constants/mui-theme';
import './input-autocomplete-content.scss';

const TextField = withStyles(MUITextField, {
  root: {
    '& input.Mui-disabled': {
      color: colors.dotmapsBlack40,
      WebkitTextFillColor: colors.dotmapsBlack40
    },
    '& label.Mui-disabled': {
      color: colors.dotmapsBlack40
    }
  }
});

// Convert legacy props to MUI v4 TextField props
const toTextFieldProps = ({
  errorText,
  floatingLabelText,
  inputStyle, // eslint-disable-line no-unused-vars
  textareaStyle, // eslint-disable-line no-unused-vars
  floatingLabelStyle, // eslint-disable-line no-unused-vars
  floatingLabelShrinkStyle, // eslint-disable-line no-unused-vars
  underlineStyle, // eslint-disable-line no-unused-vars
  underlineFocusStyle, // eslint-disable-line no-unused-vars
  style,
  value,
  ...rest
}) => ({
  ...rest,
  value,
  error: Boolean(errorText),
  helperText: errorText || null,
  ...(errorText && { FormHelperTextProps: { style: detailEdit.errors } }),
  label: floatingLabelText,
  InputLabelProps: { shrink: Boolean(value) },
  style: { width: '100%', marginTop: '1rem', ...style }
});

const InputAutocompleteContent = ({
  getInputProps,
  getSuggestionItemProps,
  inputProps,
  suggestions
}) => {
  // react-places-autocomplete's own getInputProps() already builds role="combobox" and the
  // aria-autocomplete/aria-expanded/aria-activedescendant that go with it - calling it 3 separate
  // times and keeping only onBlur/onKeyDown/onChange from each call was silently dropping all of
  // that, leaving the field with no combobox semantics at all.
  const {
    onBlur, onKeyDown, onChange, ...autocompleteInputProps
  } = getInputProps();
  // getSuggestionItemProps() assigns the SAME id (the currently-active suggestion's id) to every
  // row, which would create duplicate DOM ids - only apply it to the row that's actually active,
  // matching aria-activedescendant above to that one row.
  const activeDescendantId = autocompleteInputProps['aria-activedescendant'];

  return (
    <div data-testid="input-autocomplete-content" styleName="input-autocomplete-content-container">
      <TextField
        {...toTextFieldProps(inputProps)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onChange={onChange}
        inputProps={autocompleteInputProps}
      />
      {suggestions.length > 0 && (
        <div role="listbox" aria-label="Address suggestions" styleName="suggestions-container">
          {suggestions.map(suggestion => (
            <div
              key={suggestion.id}
              styleName={`suggestion-item ${suggestion.active ? 'suggestion-item-active' : ''}`}
              {...getSuggestionItemProps(suggestion, {})}
              {...(suggestion.active ? { id: activeDescendantId } : {})}
              aria-selected={suggestion.active}
            >
              <span>{suggestion.description}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

InputAutocompleteContent.propTypes = {
  getInputProps: PropTypes.func,
  getSuggestionItemProps: PropTypes.func,
  inputProps: PropTypes.object.isRequired,
  suggestions: PropTypes.array
};

export default InputAutocompleteContent;
